import type { TokenResponse } from '@betrayal/shared';

/**
 * Everything the app needs to know about "where am I running".
 * Mock mode (VITE_MOCK_DISCORD=1 or ?mock=<name>) fakes Discord so the whole
 * game is playable in plain browser tabs; the real path uses the Embedded App SDK.
 */
export interface Bridge {
  mode: 'mock' | 'discord';
  instanceId: string;
  user: TokenResponse['user'];
  sessionToken: string;
  /** Base path for API/WS calls ('' locally, '/.proxy' inside Discord). */
  basePath: string;
  openExternal(url: string): void;
}

const params = new URLSearchParams(location.search);

export function isMock(): boolean {
  return import.meta.env.VITE_MOCK_DISCORD === '1' || params.has('mock');
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

async function initMock(): Promise<Bridge> {
  // ?mock=Alice&instance=table1 → identity "Alice" at lobby "table1".
  const name = params.get('mock') || `Guest${Math.floor(Math.random() * 1000)}`;
  const instanceId = params.get('instance') || 'local-dev';
  const token = await post<TokenResponse>('/api/token', { mock: { id: name.toLowerCase(), name } });
  return {
    mode: 'mock',
    instanceId,
    user: token.user,
    sessionToken: token.sessionToken,
    basePath: '',
    openExternal: (url) => window.open(url, '_blank', 'noopener'),
  };
}

async function initDiscord(): Promise<Bridge> {
  const { DiscordSDK } = await import('@discord/embedded-app-sdk');
  const sdk = new DiscordSDK(import.meta.env.VITE_DISCORD_CLIENT_ID as string);
  await sdk.ready();

  const { code } = await sdk.commands.authorize({
    client_id: import.meta.env.VITE_DISCORD_CLIENT_ID as string,
    response_type: 'code',
    state: '',
    prompt: 'none',
    scope: ['identify'],
  });
  // Inside the Activity iframe every request must go through Discord's proxy.
  const token = await post<TokenResponse>('/.proxy/api/token', { code });
  await sdk.commands.authenticate({ access_token: token.discordAccessToken! });

  return {
    mode: 'discord',
    instanceId: sdk.instanceId,
    user: token.user,
    sessionToken: token.sessionToken,
    basePath: '/.proxy',
    openExternal: (url) => void sdk.commands.openExternalLink({ url }),
  };
}

export function initBridge(): Promise<Bridge> {
  return isMock() ? initMock() : initDiscord();
}
